import {createPlateEditor, Plate, PlateContainer, PlateContent, usePlateEditor} from "platejs/react";
import {defaultValue, markdownDefaultMaxLength, MarkType, NodeType} from "./utils";
import {basePlatePlugins} from "./plugins/base-plate-plugins";
import {HighlightPlugin, StrikethroughPlugin, UnderlinePlugin} from "@platejs/basic-nodes/react";
import {basePlateComponentOverrides, staticPlateComponents} from "./plugins/base-plate-component-overrides";
import styles from "./styles/SlateEditor.module.scss";
import Toolbar, {ToolbarGroup} from "./toolbar/Toolbar";
import {MarkButton} from "./toolbar/buttons/MarkButton";
import {LinkButton} from "./toolbar/buttons/LinkButton";
import {RedoButton, UndoButton} from "./toolbar/buttons/HistoryButtons";
import {FontBackgroundColorPlugin, FontColorPlugin, FontSizePlugin, TextAlignPlugin} from "@platejs/basic-styles/react";
import {FontSizeWidget} from "./toolbar/buttons/FontSizeWidget";
import {TurnIntoDropdownMenu} from "./toolbar/buttons/TurnIntoDropdownWidget";
import {AlignDropdownMenu} from "./toolbar/buttons/AlignDropdownMenu";
import {ColorDropdownMenu} from "./toolbar/buttons/ColorDropdownMenu";
import {IndentButton} from "./toolbar/buttons/IndentButton";
import {IndentPlugin} from "@platejs/indent/react";
import {OutdentButton} from "./toolbar/buttons/OutdentButton";
import {EmojiDropdownMenu} from "./toolbar/buttons/emojis/EmojiDropdownMenu";
import {EmojiPlugin} from "@platejs/emoji/react";
import emojiMartData from '@emoji-mart/data';
import {ImagePlugin, PlaceholderPlugin} from "@platejs/media/react";
import {ImageUploadButton} from "./toolbar/buttons/ImageUploadButton";
import {NodeApi, TextApi, TrailingBlockPlugin, Value} from "platejs";
import FontAwesome from "../utilities/FontAwesome";
import {ColumnItemPlugin, ColumnPlugin} from "@platejs/layout/react";
import {RefObject, useImperativeHandle, useState} from "react";
import {serializeHtml} from "platejs/static";
import classNames from "classnames";
import {columnWidthPercentageToClassName, widthFromClassList} from "./elements/Columns";
import {IframePlugin} from "./plugins/IframePlugin";
import {DivPlugin} from "./plugins/DivPlugin";
import {FloatingToolbar} from "./toolbar/FloatingToolbar";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import {ScriptPlugin} from "./plugins/ScriptPlugin";
import {toNodeProps} from "./plugins/withNodeProps";
import {Box, Button} from "@radix-ui/themes";

const editorPlugins = [
    ...basePlatePlugins,
    UnderlinePlugin,
    StrikethroughPlugin,
    FontSizePlugin,
    FontColorPlugin,
    FontBackgroundColorPlugin,
    TextAlignPlugin,
    HighlightPlugin,
    IndentPlugin,
    // @ts-ignore
    EmojiPlugin.configure({options: {data: emojiMartData}}),
    ImagePlugin.configure({
        parsers: {
            html: {
                deserializer: {
                    rules: [
                        {
                            validNodeName: 'IMG',
                        },
                    ],
                    parse: ({element, type}) => ({
                        type,
                        url: element.getAttribute('src'),
                        alt: element.getAttribute('alt'),
                    }),
                    toNodeProps: toNodeProps,
                }
            }
        }
    }),
    PlaceholderPlugin,
    TrailingBlockPlugin.configure(() => ({
        options: {
            level: 0,
            type: 'p',
        },
    })),
    DivPlugin,
    ColumnPlugin.configure({
        parsers: {
            html: {
                deserializer: {
                    parse: ({element, editor}) => {
                        if (element.classList.contains("column") && element.parentElement && element.parentElement.classList.contains("row")) {
                            // Preserve original width class - this might not be technically correct - you might have a
                            // scenario where you have four "full" classes when they should really be "fourth" but doing
                            // this prevents a jarring experience where the editor interpretation looks different than it
                            // looks on the page. All of this is really just to handle legacy page blocks where invalid column
                            // classes could be created.
                            const widthClassName = columnWidthPercentageToClassName(widthFromClassList(element.classList));

                            if (!element.getAttribute("data-width")) {
                                // the below only needs to run for the first column in the row and it will set the
                                // data-width attribute for the rest of the columns
                                const allSiblingColumns = Array.from(element.parentElement.children).filter(child => child.classList.contains("column"));
                                const totalColumns = allSiblingColumns.length;
                                let widths = allSiblingColumns.map((col) => {
                                    const parsed = Number.parseFloat(widthFromClassList(col.classList));
                                    return Number.isNaN(parsed) ? 0 : parsed;
                                });

                                const sum = widths.reduce((acc, w) => acc + w, 0);

                                if (sum !== 100) {
                                    // if sum doesn't equal 100, Plate will trigger additional normalization round-trips which can lead to
                                    // exceeding maxIterations when checking shouldNormalize within Slate
                                    const diff = 100 - sum;
                                    const adjustment = diff / totalColumns;

                                    let newSum = 0;
                                    widths = widths.map((w, idx) => {
                                        let newWidth = w + adjustment;
                                        newSum += newWidth;
                                        if (idx === widths.length - 1 && newSum !== 100) {
                                            // handle rounding error by adding difference to last column
                                            const difference = 100 - newSum;
                                            newWidth += difference;
                                        }
                                        return newWidth;
                                    });
                                }

                                widths.forEach((w, i) => {
                                    allSiblingColumns[i].setAttribute("data-width", `${w}%`);
                                });
                            }

                            return {
                                type: ColumnItemPlugin.key,
                                width: element.getAttribute("data-width"),
                                widthClassName,
                                flowOpposite: element.classList.contains("flow-opposite"),
                            }
                        } else if (element.classList.contains("row")) {
                            let isColumnContainer = false;
                            Array.from(element.children).forEach(child => {
                                if (child.classList.contains("column")) {
                                    isColumnContainer = true;
                                }
                            })
                            if (isColumnContainer) {
                                return {type: ColumnPlugin.key}
                            }
                        }
                    },
                    rules: [{validClassName: 'column'}, {validClassName: 'row'}],
                    toNodeProps,
                },
            },
        },
    }),
    IframePlugin,
    ScriptPlugin,
];

export async function serializeSlateToHtml(liveEditor, maxLength?) {
    if (liveEditor.children.length === 1 && NodeApi.string(liveEditor.children[0]) === "") {
        // If saving an empty node, just save an empty string because that's likely the intent.
        return ""
    }

    // Get the current editor state (children) from the live editor
    // Load the content into the static, override-free editor instance.
    // This uses the simplified static components (<img> for image nodes)
    const staticSerializerEditor = createPlateEditor({
        components: staticPlateComponents,
        maxLength: Math.min(maxLength, markdownDefaultMaxLength),
        value: liveEditor.children,
        plugins: editorPlugins,
        override: {
            components: staticPlateComponents,
        },
    });

    const lastElement = staticSerializerEditor.children[staticSerializerEditor.children.length - 1];
    const isTrailingBlockPluginElement = lastElement.type === NodeType.PARAGRAPH && NodeApi.string(lastElement).trim().length === 0;

    if (isTrailingBlockPluginElement) {
        // Manually remove the trailing block element created by the plugin. For some reason doing this via removeNodes API doesn't work.
        staticSerializerEditor.children = staticSerializerEditor.children.filter((c, idx) => idx < staticSerializerEditor.children.length - 1);
    }

    // Serialize the content using the static editor.
    let html = await serializeHtml(staticSerializerEditor, {
        stripClassNames: false,
        stripDataAttributes: true,
    });

    // stripDataAttributes only removes specific "data-" attributes. This removes additional slate-related attributes
    // but preserves custom attributes provided by user (commonly used by third party embeds)
    html = html.replaceAll(/ data-(?:slate-[\w-]*|block-id)="[^"]+"/g, '')

    return stripSlateClassNames(html);
}

function stripSlateClassNames(html: string) {
    return html.replaceAll(
        / class="([^"]*)"/g,
        (match: string, className: string) => {
            const classesToKeep = className
                .split(/\s+/)
                .filter((cn) => !/^slate-/.test(cn));

            return classesToKeep.length === 0
                ? ''
                : ` class="${classesToKeep.join(' ')}"`;
        }
    );
}

export function HtmlEditor(props: {
    value?: string,
    maxLength?: number,
    defaultEditor: "html" | "wysiwyg",
    ref: RefObject<any>,
    editorClassName?: string,
}) {
    const {maxLength = markdownDefaultMaxLength} = props;

    const [editRawHtml, setEditRawHtml] = useState(props.defaultEditor === "html");

    /*
    Yes, we are duplicating state here. But it's better than the subpar performance we incur when serializing to html
    on every editor value change.
     */
    const [rawHtml, setRawHtml] = useState(props.value || "");

    const editor = usePlateEditor({
        value: (editor) => {
            if (props.value) {
                editor.children = editor.api.html.deserialize({
                    element: props.value
                }) as Value;
            } else {
                editor.children = defaultValue;
            }

            if (editor.children.length === 1 && TextApi.isText(editor.children[0])) {
                // Surprisingly, Slate doesn't seem to handle plaintext as a value. This ensures that any raw text is
                // wrapped in a paragraph element.
                editor.children = [{
                    type: NodeType.PARAGRAPH,
                    children: [editor.children[0]]
                }]
            }

            return editor.children;
        },
        // prevent overriding default max length to a longer limit - default is more than enough and is in place
        // to prevent bogging down the system when converting markdown to HTML
        maxLength: Math.min(maxLength, markdownDefaultMaxLength),
        plugins: editorPlugins,
        override: {
            components: basePlateComponentOverrides,
        },
    })

    useAsyncEffect(async () => {
        if (editRawHtml) {
            setRawHtml(await serializeSlateToHtml(editor));
        }
    }, [editRawHtml])

    useImperativeHandle(props.ref, () => ({
        editor: editor,
    }), [editor])

    function forceUpdateState(html) {
        editor.tf.reset(); // important to fix issue of lag between value changing and editor adjusting selection, see https://github.com/ianstormtaylor/slate/issues/3575
        const slateValue = editor.api.html.deserialize({
            element: html,
            // preserve whitespaces, which include &nbsp; which may have been created excessively by legacy page editor
            collapseWhiteSpace: false,
        });
        // @ts-ignore
        editor.tf.setValue(slateValue);
    }

    const editorClassName = classNames({
        [styles.editor]: true,
        [props.editorClassName || ""]: props.editorClassName,
    })

    const toggleButton = (
        <Button variant={"soft"} onClick={() => setEditRawHtml(v => !v)}>
            {editRawHtml ? "Use Visual Editor" : "Edit in Raw HTML"}
        </Button>
    );

    return <div className={`${styles.container} notranslate`}>
        <Plate editor={editor}>
            {/* Raw HTML editor - always mounted, visibility toggled via CSS */}
            <div className={styles.content} style={{display: editRawHtml ? "block" : "none"}}>
                <div className={styles.toolbar}>
                    <div className={styles.toolbarRight}>
                        {toggleButton}
                    </div>
                </div>
                <Box p={"2"} className={editorClassName}>
                    <textarea
                        placeholder="Enter some HTML"
                        value={rawHtml}
                        rows={8}
                        maxLength={maxLength}
                        onChange={e => {
                            forceUpdateState(e.target.value);
                            setRawHtml(e.target.value);
                        }}
                    />
                </Box>
            </div>
            {/* Visual editor - always mounted, visibility toggled via CSS */}
            <div className={styles.content} style={{display: editRawHtml ? "none" : "block"}}>
                <Toolbar rightContent={toggleButton}>
                    <ToolbarGroup>
                        <UndoButton/>
                        <RedoButton/>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <TurnIntoDropdownMenu/>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <FontSizeWidget/>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <MarkButton format={MarkType.STRONG} tooltip={"Bold"}>
                            <FontAwesome prefix={"fas"} name={"fa-bold"}/>
                        </MarkButton>
                        <MarkButton format={MarkType.EMPHASIS} tooltip={"Italic"}>
                            <FontAwesome prefix={"fas"} name={"fa-italic"}/>
                        </MarkButton>
                        <MarkButton format={MarkType.UNDERLINE} tooltip={"Underline"}>
                            <FontAwesome prefix={"fas"} name={"fa-underline"}/>
                        </MarkButton>
                        <MarkButton format={MarkType.STRIKETHROUGH} tooltip={"Strikethrough"}>
                            <FontAwesome prefix={"fas"} name={"fa-strikethrough"}/>
                        </MarkButton>
                        <MarkButton format={MarkType.CODE} tooltip={"Code"}>
                            <FontAwesome prefix={"fas"} name={"fa-code"}/>
                        </MarkButton>
                        <ColorDropdownMenu nodeType={FontColorPlugin.key} tooltip={"Font Color"}/>
                        <ColorDropdownMenu nodeType={FontBackgroundColorPlugin.key} tooltip={"Background Color"}/>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <LinkButton showTooltip={true} />
                        <MarkButton format={MarkType.HIGHLIGHT} tooltip={"Highlight"}>
                            <FontAwesome prefix={"fas"} name={"fa-highlighter"}/>
                        </MarkButton>
                        <EmojiDropdownMenu/>
                        <ImageUploadButton/>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <AlignDropdownMenu showTooltip={true}/>
                        <OutdentButton/>
                        <IndentButton/>
                    </ToolbarGroup>
                </Toolbar>
                <FloatingToolbar>
                    <ToolbarGroup>
                        <TurnIntoDropdownMenu/>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <MarkButton format={MarkType.STRONG}>
                            <FontAwesome prefix={"fas"} name={"fa-bold"}/>
                        </MarkButton>
                        <MarkButton format={MarkType.EMPHASIS}>
                            <FontAwesome prefix={"fas"} name={"fa-italic"}/>
                        </MarkButton>
                        <MarkButton format={MarkType.UNDERLINE}>
                            <FontAwesome prefix={"fas"} name={"fa-underline"}/>
                        </MarkButton>
                        <MarkButton format={MarkType.STRIKETHROUGH}>
                            <FontAwesome prefix={"fas"} name={"fa-strikethrough"}/>
                        </MarkButton>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <LinkButton showTooltip={false}/>
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <AlignDropdownMenu showTooltip={false} />
                    </ToolbarGroup>
                </FloatingToolbar>
                <PlateContainer>
                    <div className={classNames(editorClassName, "sb-scoped-html")}>
                        <PlateContent readOnly={editRawHtml}/>
                    </div>
                </PlateContainer>
            </div>
        </Plate>
    </div>
}
